var _ = require('underscore');
var TorsoView = require('backbone-torso/modules/View');
var TorsoEvents = require('backbone-torso/modules/Events');
var insurancePolicyDetailsExtService = require('./../urls').insurancePolicyDetailsExtService;
var UseModalBehavior = require('./../widget/modal/UseModalBehavior');
var InsuranceCardModalView = require('./InsuranceCardModalView');
var insuranceFeatureConfigModel = require('./insuranceFeatureConfigModel');
var appointmentInsuranceItemTemplate = require('./appointmentInsuranceItemTemplate.hbs');
var AppointmentDataBehavior = require('./../appointment/AppointmentDataBehavior');
var InsurancePolicyDetailsDataBehavior = require('./../insurance/InsurancePolicyDetailsDataBehavior');
var ModalDialogWidget = require('./../widget/modal/ModalDialogWidget');
var wizardUtils = require('./../wizard/wizardUtils');

var MS_BETWEEN_ELIGIBILITY_RETRIEVE_CALLS = 2 * 1000;
var MS_TO_SHOW_UNKNOWN_STATUS = 10 * 1000;

/**
 * This view renders a single appointment insurance item
 * @class AppointmentInsuranceItemView
 * @constructor
 * @param options {Object} Options to configure this view
 *   @param options.context {Object} object to pass to the child views
 *     @param options.context.patientVecnaId {String} the vecna id of the patient.
 *     @param options.context.appointmentExternalId {String} the external id of the appointment being displayed.
 *     @param options.context.visitId {Number} the id of the visit being displayed.
 *     @param options.context.viewOnly {Boolean} whether to display edit (false) or just view (true) components.
 *     @param options.context.showAllDetails {Boolean} whether to display additional details (true) or minimal details (false).
 * @extends Torso.View
 */
module.exports = TorsoView.extend({
  tagName: 'li',
  className: 'data__block data__block--insurance',
  template: appointmentInsuranceItemTemplate,
  /**
   * Adds attributes to the the top level element (this is to support existing behavioral tests that query these values).
   * This only works because, as an item view, this view will only be created when the model already exists.  Values will not
   * be updated dynamically if they change for this view after it renders.
   * @returns {{data-insurance-external-id: String}} the insurance external id id.
   */
  attributes: function() {
    return {
      'data-test-insurance-external-id': this.model.get('externalId'),
      'data-test-insurance-vecna-id': this.model.get('vecnaId')
    };
  },

  events: {
    'click [data-select-insurance-checkbox]': '_selectOrDeselectInsurance',
    'click [data-set-as-primary]': '_makePrimaryInsurance',
    'click [data-photo-front]': '_showInsuranceCardFront',
    'click [data-photo-back]': '_showInsuranceCardBack'
  },

  /**
   * @property behaviors {Object}
   */
  behaviors: {
    /**
     * Behavior to allow this view to create and interact with modals.
     * @property behaviors.useModal {Object}
     */
    useModal: UseModalBehavior,
    /**
     * Behavior to access Appointment Data.
     * @property behaviors.appointment {Object}
     */
    appointment: {
      /**
       * Behavior to access Appointment Data.
       * @property behaviors.appointment.behavior {AppointmentDataBehavior}
       */
      behavior: AppointmentDataBehavior,
      /**
       * Only return a single appointment.
       * @property behaviors.appointment.returnSingleResult {Boolean}
       */
      returnSingleResult: true,
      /**
       * Render the view whenever the appointment is fetched.
       * @property behaviors.appointment.renderOnFetch {Boolean}
       */
      renderOnFetch: true,
      /**
       * Use the view._appointmentExternalId to identify the appointment to retrieve.
       * @property behaviors.pendingInsurancePolicyDetails.ids {Object}
       */
      id: { property: '_appointmentExternalId' }
    },
    /**
     * Behavior to access the current insurance policy details.
     * @property behaviors.currentInsurancePolicyDetails {Object}
     */
    currentInsurancePolicyDetails: {
      /**
       * Behavior to access Appointment Data.
       * @property behaviors.currentInsurancePolicyDetails.behavior {InsurancePolicyDetailsDataBehavior}
       */
      behavior: InsurancePolicyDetailsDataBehavior,
      /**
       * Only return a single insurance policy details.
       * @property behaviors.currentInsurancePolicyDetails.returnSingleResult {Boolean}
       */
      returnSingleResult: true,
      /**
       * Render the view whenever the current insurance policy details are fetched.
       * @property behaviors.currentInsurancePolicyDetails.renderOnFetch {Boolean}
       */
      renderOnFetch: true,
      /**
       * Use the currentInsurancePolicyDetailVecnaId from the insurance model on this view.
       * @property behaviors.currentInsurancePolicyDetails.ids {Object}
       */
      id: { property: 'model:currentInsurancePolicyDetailVecnaId' },
      alwaysFetch: true
    },
    /**
     * Behavior to access the pending insurance policy details.
     * @property behaviors.pendingInsurancePolicyDetails {Object}
     */
    pendingInsurancePolicyDetails: {
      /**
       * Behavior to access Appointment Data.
       * @property behaviors.pendingInsurancePolicyDetails.behavior {InsurancePolicyDetailsDataBehavior}
       */
      behavior: InsurancePolicyDetailsDataBehavior,
      /**
       * Only return a single insurance policy details.
       * @property behaviors.pendingInsurancePolicyDetails.returnSingleResult {Boolean}
       */
      returnSingleResult: true,
      /**
       * Render the view whenever the pending insurance policy details are fetched.
       * @property behaviors.pendingInsurancePolicyDetails.renderOnFetch {Boolean}
       */
      renderOnFetch: true,
      /**
       * Use the pendingInsurancePolicyDetailVecnaId from the insurance model on this view.
       * @property behaviors.pendingInsurancePolicyDetails.ids {Object}
       */
      id: { property: 'model:pendingInsurancePolicyDetailVecnaId' },
      alwaysFetch: true
    }
  },

  /**
   * @method initialize
   * @override
   */
  initialize: function(options) {
    _.bindAll(this, 'render', '_setSaveComplete', '_setSaveFailed', '_setSaveSuccess',
              '_fetchPolicyDetailsModel', '_clearSaveIndicators', '_waitThenClearSaveIndicators', '_fetchAppointmentModel', '_unsetRetrievingStatus', '_unsetRetrievingCoverage', '_unsetRetrievingData');

    this._patientVecnaId = options.context.patientVecnaId;
    this._appointmentExternalId = options.context.appointmentExternalId;
    this._regDate = options.context.regDate;
    this._visitId = options.context.visitId;
    this._viewOnly = options.context.viewOnly || false;
    this._showAllDetails = options.context.showAllDetails || false;
    this._insurancePolicyModel = this.model; // alias for readability.
    _.bindAll(this._insurancePolicyModel, 'fetch');

    this.set({
      showInsuranceCardFront: false,
      showInsuranceCardBack: false
    });

    this.listenTo(this.getBehavior('currentInsurancePolicyDetails'), 'fetched', this._setupCardImageModals);
    this.listenTo(this.getBehavior('pendingInsurancePolicyDetails'), 'fetched', this._setupCardImageModals);
    this._setupCardImageModals();

    this.listenTo(this.model, 'change', this.render);
    this.listenTo(this.viewState, 'change', this.render);
    this.listenTo(this.getBehavior('appointment').data, 'change:insurancePolicies', this.render);
    insuranceFeatureConfigModel.loadOnce()
      .then(this.render);

    this.set('retrievingData', true);
  },

  /**
   * Creates the modals to view the card images.
   * @method _setupCardImageModals
   * @private
   */
  _setupCardImageModals: function() {
    var insurancePolicyDetailsVecnaIdWithCard = this._getInsurancePolicyDetailsVecnaIdWithCard();
    if (insurancePolicyDetailsVecnaIdWithCard) {
      if (this._insuranceCardFrontModalView) {
        this._insuranceCardFrontModalView.dispose();
      }
      this._insuranceCardFrontModalView = new InsuranceCardModalView({
        side: InsuranceCardModalView.INSURANCE_CARD_SIDES.FRONT,
        insurancePolicyDetailsVecnaId: insurancePolicyDetailsVecnaIdWithCard
      });
      this.listenTo(this._insuranceCardFrontModalView, 'close-modal', this._closeInsuranceCardFront);

      if (this._insuranceCardBackModalView) {
        this._insuranceCardBackModalView.dispose();
      }
      this._insuranceCardBackModalView = new InsuranceCardModalView({
        side: InsuranceCardModalView.INSURANCE_CARD_SIDES.BACK,
        insurancePolicyDetailsVecnaId: insurancePolicyDetailsVecnaIdWithCard
      });
      this.listenTo(this._insuranceCardBackModalView, 'close-modal', this._closeInsuranceCardBack);
    }
  },

  /**
   * @method _activate
   * @override
   * @private
   */
  _activate: function() {
    this.listenTo(TorsoEvents, 'wizard:continue', this._saveUnsavedInsurancesToWallet);
    this._clearEligibilityInterval();
    this._clearCoverageInterval();
    this._eligibilityInterval = window.setInterval(this._unsetRetrievingStatus, MS_BETWEEN_ELIGIBILITY_RETRIEVE_CALLS);
    this._coverageInterval = window.setInterval(this._unsetRetrievingCoverage, MS_BETWEEN_ELIGIBILITY_RETRIEVE_CALLS);
  },

  /**
   * @method _deactivate
   * @override
   * @private
   */
  _deactivate: function() {
    this.stopListening(TorsoEvents, 'wizard:continue', this._saveUnsavedInsurancesToWallet);
    this._clearEligibilityInterval();
    this._clearCoverageInterval();
  },

  /**
   * @method _attach
   * @override
   * @private
   */
  _attached: function() {
    this._activate();
  },

  /**
   * @method _detach
   * @override
   * @private
   */
  _detached: function() {
    this._deactivate();
  },

  /**
   * Saves this insurance to the wallet if it is selected for this appointment and not already in the wallet.
   * @method _saveUnsavedInsurancesToWallet
   * @private
   */
  _saveUnsavedInsurancesToWallet: function() {
    var insurancePolicyInWallet = this._insurancePolicyModel.get('onWallet');
    var appointmentHasThisInsurance = this._appointmentHasThisInsurance();
    if (!insurancePolicyInWallet && appointmentHasThisInsurance) {
      this._insurancePolicyModel.save({ onWallet: true }); // not tracking, aren't updating the user if this fails.
    }
  },

  prepareFields: {
    visitId: function() {
      return this._visitId;
    },
    viewOnly: '_viewOnly',
    showAllDetails: '_showAllDetails',
    appointmentExternalId: '_appointmentExternalId',
    insurancePolicyDetailsVecnaIdWithCard: '_getInsurancePolicyDetailsVecnaIdWithCard',
    INSURANCE_CARD_SIDES: InsuranceCardModalView.INSURANCE_CARD_SIDES,
    insurancePolicyDetailsExtService: insurancePolicyDetailsExtService,
    insuranceVecnaId: function() {
      return encodeURI(this._insuranceExternalId);
    },
    selected: function() {
      var insurancePolicyVecnaId = this._insurancePolicyModel.get('vecnaId');
      var appointmentModel = this.getBehavior('appointment').data.getModel();
      var appointmentInsurance = appointmentModel && appointmentModel.getAppointmentInsurance(insurancePolicyVecnaId);
      return appointmentInsurance && !this._viewOnly;
    },
    fetchSuccess: function() {
      var currentInsuranceFetchSuccess = this.getBehavior('currentInsurancePolicyDetails').get('fetchSuccess');
      var pendingInsuranceFetchSuccess = this.getBehavior('pendingInsurancePolicyDetails').get('fetchSuccess');
      return currentInsuranceFetchSuccess && pendingInsuranceFetchSuccess;
    },
    insurancePolicyDetails: function() {
      var insurancePolicyDetailsModel = this._getInsurancePolicyDetailsModel();
      return insurancePolicyDetailsModel && insurancePolicyDetailsModel.toJSON();
    },
    insuranceCardExists: function() {
      var insurancePolicyDetailsModel = this._getInsurancePolicyDetailsModel();
      var topCardImageExists = insurancePolicyDetailsModel && insurancePolicyDetailsModel.get('topCardImageExists');
      var bottomCardImageExists = insurancePolicyDetailsModel && insurancePolicyDetailsModel.get('bottomCardImageExists');
      return topCardImageExists || bottomCardImageExists;
    },
    appointmentInsurance: function() {
      var insurancePolicyVecnaId = this._insurancePolicyModel.get('vecnaId');
      var appointmentModel = this.getBehavior('appointment').data.getModel();
      var appointmentInsurance = appointmentModel && appointmentModel.getAppointmentInsurance(insurancePolicyVecnaId);
      if (appointmentInsurance) {
        appointmentInsurance.index = appointmentModel.getAppointmentInsuranceIndex(insurancePolicyVecnaId);
      }
      return appointmentInsurance;
    },
    insuranceEligibilityEnabled: function() {
      return insuranceFeatureConfigModel.isInsuranceEligibilityEnabled(insuranceFeatureConfigModel.CONFIG_LEVEL.NORMAL);
    },
    insuranceEditUrl: function() {
      return wizardUtils.getPageUrl({
        hash: 'appointmentPreRegInsuranceEdit',
        appointmentExternalId: this._appointmentExternalId,
        regDate: this._regDate,
        name: this.model.get('externalId')
      });
    }
  },

  /**
   * @method postrender
   * @override
   */
  postrender: function() {
    var appointmentHasThisInsurance = this._appointmentHasThisInsurance();
    if (appointmentHasThisInsurance) {
      this.$el.addClass('data__block--selected');
    } else {
      this.$el.removeClass('data__block--selected');
    }
    // Fix for checkboxes so the javascript objects are consistent with their DOM state.
    this.$el.find('[data-select-insurance-checkbox-input]').prop('checked', appointmentHasThisInsurance);
    var retrievingData = this.get('retrievingData');
    if (retrievingData) {
      window.setTimeout(this._unsetRetrievingData, MS_TO_SHOW_UNKNOWN_STATUS);
    }
  },

  /**
   * clears the interval set on eligibility status
   * @method _clearEligibilityInterval
   */
  _clearEligibilityInterval: function() {
    if(this._eligibilityInterval) {
      window.clearInterval(this._eligibilityInterval);
    }
  },

  /**
   * clears the interval set on eligibility coverage
   * @method _clearCoverageInterval
   */
  _clearCoverageInterval: function() {
    if(this._coverageInterval) {
      window.clearInterval(this._coverageInterval);
    }
  },

  /**
   * Returns the pending insurance details model if it exists, otherwise the current details model is returned.
   * @method _getInsurancePolicyDetailsModel
   * @returns {Backbone.Model} with either the pending or current details.
   * @private
   */
  _getInsurancePolicyDetailsModel: function() {
    var currentInsurancePolicyDetails = this.getBehavior('currentInsurancePolicyDetails').data.getModel();
    var pendingInsurancePolicyDetails = this.getBehavior('pendingInsurancePolicyDetails').data.getModel();
    return pendingInsurancePolicyDetails || currentInsurancePolicyDetails;
  },

  /**
   * Return either the pending or current insurance policy details' vecna id if it has a card image.
   * Checks the pending insurance for updates first, then the current policy details.
   * @method _getInsurancePolicyDetailsVecnaIdWithCard
   * @returns {String} the vecna id of the insurance policy details with a card image, or undefined if neither have card images.
   * @private
   */
  _getInsurancePolicyDetailsVecnaIdWithCard: function() {
    var pendingInsurancePolicyDetailsModel = this.getBehavior('pendingInsurancePolicyDetails').data.getModel();
    var insurancePolicyDetailsVecnaIdWithCard = this._getInsurancePolicyDetailsVecnaIdIfHasCard(pendingInsurancePolicyDetailsModel);
    if (!insurancePolicyDetailsVecnaIdWithCard) {
      var currentInsurancePolicyDetailsModel = this.getBehavior('currentInsurancePolicyDetails').data.getModel();
      insurancePolicyDetailsVecnaIdWithCard = this._getInsurancePolicyDetailsVecnaIdIfHasCard(currentInsurancePolicyDetailsModel);
    }
    return insurancePolicyDetailsVecnaIdWithCard;
  },

  /**
   * Return the vecna id of the insurance policy details if it has a card image on it.
   * @method _getInsurancePolicyDetailsVecnaIdIfHasCard
   * @param insurancePolicyInfoDetailsModel {InsurancePolicyDetailsModel} the details model to check for the card image on.
   * @returns {String} the vecna id of the insurance if the card image exists,
   *                   or undefined if the model doesn't have a card image or doesn't have a vecna id.
   * @private
   */
  _getInsurancePolicyDetailsVecnaIdIfHasCard: function(insurancePolicyInfoDetailsModel) {
    var hasTopCard = insurancePolicyInfoDetailsModel && insurancePolicyInfoDetailsModel.get('topCardImageExists');
    var hasBottomCard = insurancePolicyInfoDetailsModel && insurancePolicyInfoDetailsModel.get('bottomCardImageExists');
    var hasCard = hasTopCard || hasBottomCard;
    return hasCard && insurancePolicyInfoDetailsModel.get('vecnaId');
  },

  /**
   * Called when clicking on the checkbox, needs to add or remove the insurance from the appointment.
   * @method _selectOrDeselectInsurance
   * @private
   */
  _selectOrDeselectInsurance: function(event) {
    event.preventDefault();
    var insuranceVecnaId = this._insurancePolicyModel.get('vecnaId');
    var appointmentModel = this.getBehavior('appointment').data.getModel();
    if (this._appointmentHasThisInsurance()) {
      this._showConfirmationModal();
    } else {
      appointmentModel.addAppointmentInsurance(insuranceVecnaId);
      this._saveAppointmentModel(appointmentModel);
    }
  },

  /**
   * Show the removing insurance confirmation modal
   * @method _showConfirmationModal
   * @private
   */
  _showConfirmationModal: function() {
    this._confirmationModalView = new ModalDialogWidget({
      dataViewName: 'remove-insurance-confirmation-modal',
      headerKey: 'insurance.insuranceRemovalConfirmationModal.header',
      descriptionKey: 'insurance.insuranceRemovalConfirmationModal.body',
      yesKey: 'insurance.insuranceRemovalConfirmationModal.removeInsuranceButton',
      noKey: 'insurance.insuranceRemovalConfirmationModal.cancelRemove'
    });
    this.showModal(this._confirmationModalView, _.bind(this._removeAppointmentInsurance, this));
  },

  /**
   * Remove insurance from appointment
   * @method _removeAppointmentInsurance
   * @private
   */
  _removeAppointmentInsurance: function() {
    var insuranceVecnaId = this._insurancePolicyModel.get('vecnaId');
    var appointmentModel = this.getBehavior('appointment').data.getModel();
    appointmentModel.removeAppointmentInsurance(insuranceVecnaId);
    this._saveAppointmentModel(appointmentModel);
  },

  /**
   * Saves the appointment model and keeps track of the save status on the view state.
   * @method _saveAppointmentModel
   * @param appointmentModel {AppointmentModel} to save
   * @private
   */
  _saveAppointmentModel: function(appointmentModel) {
    this._clearSaveIndicators();
    this.set('saveInProgress', true);
    appointmentModel.save()
      .then(this._setSaveSuccess, this._setSaveFailed)
      .then(this._setSaveComplete, this._setSaveComplete)
      .then(this._fetchAppointmentModel, this._fetchAppointmentModel)
      .then(this._insurancePolicyModel.fetch, this._insurancePolicyModel.fetch) // .then is used to chain promises.
      .then(this._fetchPolicyDetailsModel, this._fetchPolicyDetailsModel) // .then is used to chain promises.
      .then(this._waitThenClearSaveIndicators, this._waitThenClearSaveIndicators);
  },

  /**
   * Convenience method for promises that want to mark the saving process as completed.
   * @method _setSaveComplete
   * @private
   */
  _setSaveComplete: function() {
    this.set('saveInProgress', false);
  },

  /**
   * Convenience method for promises that want to mark the saving process as failed.
   * @method _setSaveFailed
   * @private
   */
  _setSaveFailed: function() {
    this.set('saveFailed', true);
  },

  /**
   * Convenience method for promises that want to mark the saving process as successful.
   * @method _setSaveSuccess
   * @private
   */
  _setSaveSuccess: function() {
    this.set('saveSuccess', true);
  },

  /**
   * Convenience method for fetching the appointment model.
   * @method _fetchAppointmentModel
   * @private
   */
  _fetchAppointmentModel: function() {
    this.getBehavior('appointment').fetch();
  },

  /**
   * Convenience method for fetching the insurance policy details model from services.
   * @method _fetchPolicyDetailsModel
   * @private
   */
  _fetchPolicyDetailsModel: function() {
    var insuranceDetailsModel = this._getInsurancePolicyDetailsModel();
    if (insuranceDetailsModel) {
      return insuranceDetailsModel.fetch();
    }
  },

  /**
   * Clears the indicators that identify saving the appointment and the result of the action.
   * @method _clearSaveIndicators
   * @private
   */
  _clearSaveIndicators: function() {
    clearTimeout(this._clearSaveIndicatorsTimeoutId);
    this.set('saveFailed', false);
    this.set('saveSuccess', false);
    this.set('saveInProgress', false);
  },

  /**
   * Waits for a few seconds before clearing the save indicators.  So the save success/failure messages can be setup to disappear.
   * @method _waitThenClearSaveIndicators
   * @private
   */
  _waitThenClearSaveIndicators: function() {
    clearTimeout(this._clearSaveIndicatorsTimeoutId);
    var tenSecondsInMs = 10 * 1000;
    this._clearSaveIndicatorsTimeoutId = setTimeout(this._clearSaveIndicators, tenSecondsInMs);
  },

  /**
   * @method _appointmentHasThisInsurance
   * @returns {Boolean} true if the appointment has this insurance on it,
   *                    false if the appointment hasn't been fetched or the insurance doesn't exist on that appointment.
   * @private
   */
  _appointmentHasThisInsurance: function() {
    var appointmentModel = this.getBehavior('appointment').data.getModel();
    var insuranceVecnaId = this._insurancePolicyModel.get('vecnaId');
    var insuranceOnAppointment = appointmentModel && appointmentModel.hasAppointmentInsurance(insuranceVecnaId);
    return insuranceOnAppointment;
  },

  /**
   * Actually makes an insurance primary
   *
   * @method _makePrimaryInsurance
   * @param event {jQuery.Event} the event that called this method.
   * @private
   */
  _makePrimaryInsurance: function(event) {
    event.preventDefault();
    var appointmentModel = this.getBehavior('appointment').data.getModel();
    var insuranceVecnaId = this._insurancePolicyModel.get('vecnaId');
    appointmentModel.addAppointmentInsurance(insuranceVecnaId);
    this._saveAppointmentModel(appointmentModel);
  },

  /**
   * Shows the insurance card front view.
   * @method _showInsuranceCardFront
   * @private
   */
  _showInsuranceCardFront: function() {
    var insurancePolicyDetailsVecnaIdWithCard = this._getInsurancePolicyDetailsVecnaIdWithCard();
    if (insurancePolicyDetailsVecnaIdWithCard) {
      this._insuranceCardFrontModalView = new ModalDialogWidget({
        dataViewName: 'view insurance card front image',
        className: 'brec-dialog brec-dialog--regular',
        headerKey: 'insurance.appointmentInsurance.insuranceCardModal.frontImageHeader',
        alertOnly: true,
        yesKey: 'modalWidget.defaultCloseKey',
        trackedView: new InsuranceCardModalView({
          side: InsuranceCardModalView.INSURANCE_CARD_SIDES.FRONT,
          insurancePolicyDetailsVecnaId: insurancePolicyDetailsVecnaIdWithCard
        })
      });
    }
    this.showModal(this._insuranceCardFrontModalView);
  },

  /**
   * Shows the insurance card back view.
   * @method _showInsuranceCardBack
   * @private
   */
  _showInsuranceCardBack: function() {
    var insurancePolicyDetailsVecnaIdWithCard = this._getInsurancePolicyDetailsVecnaIdWithCard();
    if (insurancePolicyDetailsVecnaIdWithCard) {
      this._insuranceCardBackModalView = new ModalDialogWidget({
        dataViewName: 'view insurance card back image',
        className: 'brec-dialog brec-dialog--regular',
        headerKey: 'insurance.appointmentInsurance.insuranceCardModal.backImageHeader',
        alertOnly: true,
        yesKey: 'modalWidget.defaultCloseKey',
        trackedView: new InsuranceCardModalView({
          side: InsuranceCardModalView.INSURANCE_CARD_SIDES.BACK,
          insurancePolicyDetailsVecnaId: insurancePolicyDetailsVecnaIdWithCard
        })
      });
    }
    this.showModal(this._insuranceCardBackModalView);
  },

  /**
   * Unsets 'retrievingCoverage'
   * @method _unsetRetrievingCoverage
   * @private
   */
  _unsetRetrievingCoverage: function() {
    var insurancePolicyVecnaId = this._insurancePolicyModel.get('vecnaId');
    var appointmentModel = this.getBehavior('appointment').data.getModel();
    var appointmentInsurance = appointmentModel && appointmentModel.getAppointmentInsurance(insurancePolicyVecnaId);
    var coverage;
    if (appointmentInsurance) {
      coverage = appointmentInsurance.eligibilityCoverage;
    }
    if (!this._viewOnly && (_.isUndefined(coverage) || _.isNull(coverage))) {
      this.getBehavior('appointment').fetch();
    } else {
      this._clearCoverageInterval();
    }
  },

  /**
   * Unsets 'retrievingStatus'
   * @method _unsetRetrievingStatus
   * @private
   */
  _unsetRetrievingStatus: function() {
    var status = this.model.get('eligibilityStatus');
    if(!this._viewOnly && (_.isUndefined(status) || _.isNull(status))) {
      this.model.fetch();
    } else {
      this._clearEligibilityInterval();
    }
  },

  /**
   * Unsets 'retrievingData'
   * @method _unsetRetrievingData
   * @private
   */
  _unsetRetrievingData: function() {
    this.viewState.unset('retrievingData');
  }
});
